import { type ResultOf, useSession } from '@horof/shared';
import { graphql } from './gql';

/** The water side of the desk (doc 39): prices and settings. */

export const PricingDocument = graphql(`
  query DeskPricing($consumptions: [Int]!) {
    billingConfigurations(orderBy: [EFFECTIVE_FROM_DESC]) {
      nodes {
        id
        name
        description
        billingMethod
        effectiveFrom
        effectiveTo
        status
        inUse
        shartUnitPrice
        bills {
          totalCount
        }
        consumptionTiers(orderBy: [TIER_START_ASC]) {
          nodes {
            id
            tierStart
            tierEnd
            rate
            description
          }
        }
        feeConfigurations(orderBy: [ID_ASC]) {
          nodes {
            id
            name
            amount
            feeType
            onlyWhenNoConsumption
            description
          }
        }
        preview(consumptions: $consumptions) {
          consumption
          total
        }
      }
    }
  }
`);

export type Config = NonNullable<
  NonNullable<
    ResultOf<typeof PricingDocument>['billingConfigurations']
  >['nodes'][number]
>;

/** What bills would cost with unsaved prices (computed by arqam). */
export const BillingPreviewDocument = graphql(`
  query DeskBillingPreview(
    $billingMethod: BillingMethodType!
    $tiers: [BillingTierInput]!
    $fees: [BillingFeeInput]
    $consumptions: [Int]!
  ) {
    billingPreview(
      billingMethod: $billingMethod
      tiers: $tiers
      fees: $fees
      consumptions: $consumptions
    ) {
      consumption
      consumptionPrice
      totalFees
      total
    }
  }
`);

export const SaveBillingConfigurationDocument = graphql(`
  mutation DeskSaveBillingConfiguration(
    $input: AppSaveBillingConfigurationInput!
  ) {
    appSaveBillingConfiguration(input: $input) {
      result {
        id
        effectiveFrom
        status
      }
    }
  }
`);

export const RenameBillingConfigurationDocument = graphql(`
  mutation DeskRenameBillingConfiguration(
    $configId: Int!
    $name: String!
    $description: String
  ) {
    appRenameBillingConfiguration(
      input: { configId: $configId, name: $name, description: $description }
    ) {
      result {
        id
        name
        description
      }
    }
  }
`);

export const DeleteBillingConfigurationDocument = graphql(`
  mutation DeskDeleteBillingConfiguration($configId: Int!) {
    appDeleteBillingConfiguration(input: { configId: $configId }) {
      result {
        id
      }
    }
  }
`);

export const SettingsDocument = graphql(`
  query DeskSettings {
    currentSettings {
      associationName
      address
      contactPhone
      dueDays
      firstReminderAfterDays
      reminderEveryDays
      publicArrearsAfterDays
      publicArrearsRedAfterDays
      publicArrearsMinAmount
      publicArrearsSince
      readingPhotoAlways
      receiptPhotoMinAmount
    }
  }
`);

export const UpdateSettingsDocument = graphql(`
  mutation DeskUpdateSettings($patch: SettingPatch!) {
    updateSetting(input: { id: true, patch: $patch }) {
      setting {
        associationName
        address
        contactPhone
        dueDays
        firstReminderAfterDays
        reminderEveryDays
        publicArrearsAfterDays
        publicArrearsRedAfterDays
        publicArrearsMinAmount
        publicArrearsSince
        readingPhotoAlways
        receiptPhotoMinAmount
      }
    }
  }
`);

export const pricingContext = { additionalTypenames: ['BillingConfiguration'] };

/** Prices and settings are the committee's (arqam doc 42). */
export function useIsCommittee(): boolean {
  return useSession().roles.includes('committee');
}
